import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Workflow, Plus, Loader2, Play, Pause, Square, Trash2, ChevronDown, ChevronUp, X, FolderKanban } from "lucide-react";
import { toast } from "sonner";

const PURPLE = "#8A52FF";

const SWARM_TEMPLATES = [
  {
    name: "Product Launch Campaign",
    goal: "Plan and produce a complete product launch campaign: positioning, launch plan, announcement copy, social posts and a launch checklist.",
    emojiColor: "#FF3AA8",
    agents: [
      { name: "Market Research Agent", role: "research", instructions: "Research the market, audience, competitors and positioning opportunities for this launch." },
      { name: "Launch Strategist", role: "strategy", instructions: "Build the launch plan: phases, channels, timeline, KPIs." },
      { name: "Copy Agent", role: "creation", instructions: "Write the announcement, landing hero copy, email and 5 social posts." },
      { name: "Review Agent", role: "review", instructions: "Review everything for consistency and impact; output the final polished campaign." },
    ],
  },
  {
    name: "Content Creation Swarm",
    goal: "Create a complete social media content package for the brand: content pillars, 2-week calendar, captions and hooks.",
    emojiColor: "#00DC82",
    agents: [
      { name: "Audience Agent", role: "research", instructions: "Define audience personas, pains and content angles that resonate." },
      { name: "Content Strategist", role: "strategy", instructions: "Define content pillars and a 2-week posting calendar." },
      { name: "Copywriter Agent", role: "creation", instructions: "Write captions, hooks and CTAs for every calendar slot." },
      { name: "Editor Agent", role: "review", instructions: "Tighten every caption, ensure brand voice, output the final package." },
    ],
  },
  {
    name: "Market Research Deep-Dive",
    goal: "Produce a deep market research report: landscape, competitors, trends, opportunities and strategic recommendations.",
    emojiColor: "#FFD23F",
    agents: [
      { name: "Landscape Agent", role: "research", instructions: "Map the market landscape, size, segments and key players." },
      { name: "Competitor Agent", role: "research", instructions: "Analyze top competitors: offers, pricing, strengths, weaknesses." },
      { name: "Trends Agent", role: "research", instructions: "Identify current and emerging trends shaping this market." },
      { name: "Synthesis Agent", role: "review", instructions: "Merge all findings into an executive-ready report with recommendations." },
    ],
  },
  {
    name: "Brand Identity Sprint",
    goal: "Develop a brand identity kit: positioning statement, voice and tone guide, tagline options and messaging framework.",
    emojiColor: "#00E5FF",
    agents: [
      { name: "Discovery Agent", role: "research", instructions: "Clarify the brand's audience, values and differentiation." },
      { name: "Positioning Agent", role: "strategy", instructions: "Craft the positioning statement and messaging framework." },
      { name: "Voice Agent", role: "creation", instructions: "Define voice & tone guide plus 10 tagline options." },
      { name: "Review Agent", role: "review", instructions: "Refine everything into a cohesive final brand kit." },
    ],
  },
];
const STATUS_COLORS = {
  queued: "#9DA3B4", running: "#8A52FF", paused: "#FFD23F",
  completed: "#00DC82", failed: "#F87171", cancelled: "#9DA3B4",
  waiting: "#6F7585",
};

function StatusChip({ status }) {
  return (
    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border"
      style={{ color: STATUS_COLORS[status] || "#9DA3B4", borderColor: `${STATUS_COLORS[status] || "#9DA3B4"}55` }}>
      {status}
    </span>
  );
}

export default function SwarmCenter() {
  const { refreshUser } = useAuth();
  const [swarms, setSwarms] = useState([]);
  const [runs, setRuns] = useState([]);
  const [view, setView] = useState({ mode: "empty" }); // empty | create | swarm | run
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadAll = () => {
    api.get("/swarms").then((r) => setSwarms(r.data.items)).catch(() => {});
    api.get("/swarms/runs").then((r) => setRuns(r.data.items)).catch(() => {});
  };
  useEffect(() => { loadAll(); return () => clearInterval(pollRef.current); }, []);

  // Poll the open run while active
  useEffect(() => {
    clearInterval(pollRef.current);
    if (view.mode === "run" && ["queued", "running", "paused"].includes(view.run?.status)) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/swarms/runs/${view.run.run_id}`);
          setView((v) => (v.mode === "run" && v.run.run_id === data.run_id ? { ...v, run: data } : v));
          if (!["queued", "running", "paused"].includes(data.status)) { loadAll(); refreshUser(); }
        } catch { /* transient */ }
      }, 2500);
    }
    return () => clearInterval(pollRef.current);
  }, [view.mode, view.run?.run_id, view.run?.status]);

  const openRun = async (run_id) => {
    try { const { data } = await api.get(`/swarms/runs/${run_id}`); setView({ mode: "run", run: data }); }
    catch { toast.error("Could not open run"); }
  };
  const control = async (run_id, action) => {
    try { await api.post(`/swarms/runs/${run_id}/${action}`); openRun(run_id); }
    catch (e) { toast.error(e?.response?.data?.detail || `Could not ${action}`); }
  };
  const launch = async (swarm_id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/swarms/${swarm_id}/runs`);
      toast.success("Swarm run launched");
      refreshUser(); loadAll();
      setView({ mode: "run", run: data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Launch failed"); }
    finally { setBusy(false); }
  };
  const removeSwarm = async (swarm_id) => {
    await api.delete(`/swarms/${swarm_id}`).catch(() => {});
    loadAll(); setView({ mode: "empty" });
  };

  const applyTemplate = async (t) => {
    setBusy(true);
    try {
      const { data } = await api.post("/swarms", { name: t.name, goal: t.goal, agents: t.agents });
      toast.success(`"${t.name}" swarm ready — launch when you are`);
      loadAll();
      setView({ mode: "swarm", swarm: data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create swarm"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto kr-page" data-module="swarm">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline" style={{ color: PURPLE }}>[ SWARM CENTER ]</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Swarm Center</h1>
          <p className="text-[#9DA3B4] text-sm mt-1.5">Run AI agent swarms to get work done — define agents, launch runs, review real outputs.</p>
        </div>
        <Button onClick={() => setView({ mode: "create" })} className="rounded-xl h-10 px-5 font-semibold text-white border-0" style={{ background: PURPLE }} data-testid="swarm-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Swarm
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: swarms + runs */}
        <div className="lg:col-span-4 space-y-4">
          <div className="kr-card p-4">
            <div className="kr-overline mb-3">MY SWARMS</div>
            <div className="space-y-2">
              {swarms.map((s) => (
                <button key={s.swarm_id} onClick={() => setView({ mode: "swarm", swarm: s })}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${view.swarm?.swarm_id === s.swarm_id ? "border-[#8A52FF]/50 bg-[#8A52FF]/5" : "border-white/[0.07] hover:border-white/20"}`}
                  data-testid={`swarm-item-${s.swarm_id}`}>
                  <div className="text-sm font-semibold truncate">{s.name}</div>
                  <div className="text-xs text-white/40 truncate mt-0.5">{s.goal}</div>
                </button>
              ))}
              {swarms.length === 0 && <div className="text-xs text-white/35 py-4 text-center">No swarms yet — create one.</div>}
            </div>
          </div>
          <div className="kr-card p-4">
            <div className="kr-overline mb-3">RECENT RUNS</div>
            <div className="space-y-2">
              {runs.slice(0, 8).map((r) => (
                <button key={r.run_id} onClick={() => openRun(r.run_id)}
                  className="w-full text-left p-3 rounded-lg border border-white/[0.07] hover:border-white/20 flex items-center gap-3"
                  data-testid={`swarm-run-item-${r.run_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{r.swarm_name || r.goal}</div>
                    <div className="text-[11px] text-white/35">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <StatusChip status={r.status} />
                </button>
              ))}
              {runs.length === 0 && <div className="text-xs text-white/35 py-4 text-center">No runs yet.</div>}
            </div>
          </div>
        </div>

        {/* Right: detail */}
        <div className="lg:col-span-8">
          {view.mode === "empty" && (
            <div className="space-y-4">
              <div className="kr-card p-6">
                <div className="flex items-center gap-3">
                  <Workflow className="h-6 w-6" style={{ color: PURPLE }} />
                  <div>
                    <div className="font-display text-lg">Swarm Templates</div>
                    <div className="text-xs text-white/45">Proven recipes — one tap creates the swarm with agents pre-configured.</div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="swarm-templates">
                  {SWARM_TEMPLATES.map((t) => (
                    <button
                      key={t.name}
                      onClick={() => applyTemplate(t)}
                      disabled={busy}
                      className="text-left p-4 rounded-xl border transition-all hover:-translate-y-0.5"
                      style={{ borderColor: `${t.emojiColor}40`, background: `linear-gradient(150deg, ${t.emojiColor}12, rgba(16,18,25,0.6) 60%)` }}
                      data-testid={`swarm-template-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: t.emojiColor }} />
                        <span className="text-sm font-semibold">{t.name}</span>
                      </div>
                      <p className="text-xs text-white/45 mt-2 line-clamp-2">{t.goal}</p>
                      <div className="text-[11px] font-mono mt-3" style={{ color: t.emojiColor }}>{t.agents.length} agents · tap to create</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="kr-card p-8 flex flex-col items-center justify-center text-center">
                <div className="text-sm text-white/45 max-w-sm">Or build your own: create a swarm, define its goal and agents, then launch a run. Each agent works in sequence and you get one final synthesized result.</div>
              </div>
            </div>
          )}
          {view.mode === "create" && <SwarmForm onDone={(s) => { loadAll(); setView({ mode: "swarm", swarm: s }); }} onCancel={() => setView({ mode: "empty" })} />}
          {view.mode === "swarm" && (
            <SwarmDetail swarm={view.swarm} runs={runs.filter((r) => r.swarm_id === view.swarm.swarm_id)}
              onLaunch={() => launch(view.swarm.swarm_id)} busy={busy} onOpenRun={openRun} onDelete={() => removeSwarm(view.swarm.swarm_id)} />
          )}
          {view.mode === "run" && <RunDetail run={view.run} onControl={control} onBack={() => setView({ mode: "empty" })} />}
        </div>
      </div>
    </div>
  );
}

function SwarmForm({ onDone, onCancel }) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [agents, setAgents] = useState([
    { name: "Research Agent", role: "research", instructions: "Investigate the goal deeply — facts, context, constraints." },
    { name: "Strategy Agent", role: "strategy", instructions: "Turn research into a clear, prioritized plan." },
    { name: "Creation Agent", role: "creation", instructions: "Produce the actual deliverables described by the goal." },
    { name: "Review Agent", role: "review", instructions: "Critically review and output a final improved version." },
  ]);
  const [saving, setSaving] = useState(false);

  const setAgent = (i, k, v) => setAgents((a) => a.map((ag, j) => (j === i ? { ...ag, [k]: v } : ag)));
  const submit = async () => {
    if (!name.trim() || !goal.trim()) return toast.error("Name and goal are required");
    if (agents.some((a) => !a.name.trim())) return toast.error("Every agent needs a name");
    setSaving(true);
    try {
      const { data } = await api.post("/swarms", { name, goal, agents });
      toast.success("Swarm created");
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create swarm"); }
    finally { setSaving(false); }
  };

  return (
    <div className="kr-card p-6" data-testid="swarm-create-form">
      <div className="flex items-center justify-between">
        <div className="font-display text-lg">New Swarm</div>
        <button onClick={onCancel} className="text-white/40 hover:text-white" data-testid="swarm-form-cancel"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-4 space-y-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Swarm name — e.g. Content Creation Swarm" className="bg-[#07080B] border-white/10" data-testid="swarm-name-input" />
        <Textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={3} placeholder="Main goal — e.g. Create a complete social media campaign for a product launch" className="bg-[#07080B] border-white/10 resize-none" data-testid="swarm-goal-input" />
        <div className="kr-overline">AGENTS</div>
        {agents.map((a, i) => (
          <div key={i} className="p-4 rounded-lg border border-white/[0.08] space-y-2.5" data-testid={`swarm-agent-editor-${i}`}>
            <div className="flex gap-2">
              <Input value={a.name} onChange={(e) => setAgent(i, "name", e.target.value)} placeholder="Agent name" className="bg-[#07080B] border-white/10 flex-1" />
              <Input value={a.role} onChange={(e) => setAgent(i, "role", e.target.value)} placeholder="Role" className="bg-[#07080B] border-white/10 w-32" />
              <button onClick={() => setAgents((ag) => ag.filter((_, j) => j !== i))} className="text-white/35 hover:text-red-300 px-1" aria-label="Remove agent"><Trash2 className="h-4 w-4" /></button>
            </div>
            <Textarea value={a.instructions} onChange={(e) => setAgent(i, "instructions", e.target.value)} rows={2} placeholder="Instructions for this agent" className="bg-[#07080B] border-white/10 resize-none text-sm" />
          </div>
        ))}
        <Button variant="outline" onClick={() => setAgents((a) => [...a, { name: "", role: "specialist", instructions: "" }])} className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="swarm-add-agent-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add agent
        </Button>
        <Button onClick={submit} disabled={saving} className="w-full rounded-xl h-11 font-semibold text-white border-0" style={{ background: PURPLE }} data-testid="swarm-create-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create swarm"}
        </Button>
      </div>
    </div>
  );
}

function SwarmDetail({ swarm, runs, onLaunch, busy, onOpenRun, onDelete }) {
  return (
    <div className="kr-card p-6" data-testid="swarm-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-xl">{swarm.name}</div>
          <p className="text-sm text-white/55 mt-1.5">{swarm.goal}</p>
        </div>
        <button onClick={onDelete} className="text-white/35 hover:text-red-300 shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center" aria-label="Delete swarm" data-testid="swarm-delete-btn"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {swarm.agents.map((a) => (
          <div key={a.agent_id} className="p-3.5 rounded-lg border border-white/[0.08]">
            <div className="text-sm font-semibold">{a.name}</div>
            <div className="text-[11px] font-mono uppercase tracking-wider mt-0.5" style={{ color: PURPLE }}>{a.role}</div>
            <p className="text-xs text-white/45 mt-1.5 line-clamp-2">{a.instructions}</p>
          </div>
        ))}
      </div>
      <Button onClick={onLaunch} disabled={busy} className="mt-5 rounded-xl h-11 px-6 font-semibold text-white border-0" style={{ background: PURPLE }} data-testid="swarm-launch-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-2" /> Launch run · 20 cr</>}
      </Button>
      {runs.length > 0 && (
        <div className="mt-6">
          <div className="kr-overline mb-2">RUNS OF THIS SWARM</div>
          {runs.map((r) => (
            <button key={r.run_id} onClick={() => onOpenRun(r.run_id)} className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.04] text-left">
              <span className="text-xs text-white/45 flex-1">{new Date(r.created_at).toLocaleString()}</span>
              <StatusChip status={r.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RunDetail({ run, onControl, onBack }) {
  const [saveOpen, setSaveOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const active = ["queued", "running", "paused"].includes(run.status);

  useEffect(() => {
    if (saveOpen && projects.length === 0) api.get("/projects").then((r) => setProjects(r.data.items || [])).catch(() => {});
  }, [saveOpen]);

  const save = async () => {
    if (!projectId) return toast.error("Pick a project");
    try { await api.post(`/swarms/runs/${run.run_id}/save-to-project`, { project_id: projectId }); toast.success("Saved to project"); setSaveOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="kr-card p-6" data-testid="swarm-run-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="font-display text-xl truncate">{run.swarm_name || "Swarm run"}</div>
            <StatusChip status={run.status} />
          </div>
          <p className="text-sm text-white/55 mt-1.5">Goal: {run.goal}</p>
          <div className="text-[11px] text-white/35 mt-1">
            Started {run.started_at ? new Date(run.started_at).toLocaleString() : "—"} · Credits {run.credits_used}
            {run.completed_at && <> · Finished {new Date(run.completed_at).toLocaleString()}</>}
          </div>
        </div>
        <div className="flex gap-2">
          {active && run.status !== "paused" && (
            <Button variant="outline" onClick={() => onControl(run.run_id, "pause")} className="border-white/10 hover:bg-white/5 hover:text-white h-9" data-testid="swarm-run-pause"><Pause className="h-3.5 w-3.5 mr-1.5" /> Pause</Button>
          )}
          {run.status === "paused" && (
            <Button variant="outline" onClick={() => onControl(run.run_id, "resume")} className="border-white/10 hover:bg-white/5 hover:text-white h-9" data-testid="swarm-run-resume"><Play className="h-3.5 w-3.5 mr-1.5" /> Resume</Button>
          )}
          {active && (
            <Button variant="outline" onClick={() => onControl(run.run_id, "cancel")} className="border-red-400/25 text-red-300 hover:bg-red-500/10 hover:text-red-200 h-9" data-testid="swarm-run-cancel"><Square className="h-3.5 w-3.5 mr-1.5" /> Cancel</Button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="mt-5">
        <div className="flex items-center justify-between text-xs text-white/45 mb-1.5">
          <span>{run.stage || "Waiting"}</span><span className="font-mono">{run.progress || 0}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${run.progress || 0}%`, background: "linear-gradient(90deg,#8A52FF,#FF3AA8)" }} data-testid="swarm-run-progress" />
        </div>
      </div>
      {run.error && <div className="mt-3 text-xs text-red-300 border border-red-400/25 rounded-lg p-3" data-testid="swarm-run-error">{run.error}</div>}

      {/* Agent tasks */}
      <div className="mt-5 space-y-2.5">
        {run.tasks.map((t, i) => <AgentTask key={i} t={t} />)}
      </div>

      {run.final_output && (
        <div className="mt-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="kr-overline" style={{ color: "#00DC82" }}>FINAL RESULT</div>
            <div className="flex items-center gap-2">
              {!saveOpen ? (
                <Button variant="outline" onClick={() => setSaveOpen(true)} className="border-white/10 hover:bg-white/5 hover:text-white h-8 text-xs" data-testid="swarm-run-save-btn"><FolderKanban className="h-3.5 w-3.5 mr-1.5" /> Save to project</Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger className="bg-[#07080B] border-white/10 h-8 w-44 text-xs" data-testid="swarm-save-project-select"><SelectValue placeholder="Pick project…" /></SelectTrigger>
                    <SelectContent className="bg-[#101219] border-white/10">
                      {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id} className="focus:bg-white/5">{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={save} className="h-8 text-xs text-white border-0" style={{ background: PURPLE }} data-testid="swarm-save-confirm">Save</Button>
                </div>
              )}
            </div>
          </div>
          <pre className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-white/85 font-body bg-[#07080B] border border-white/[0.07] rounded-xl p-4 max-h-[480px] overflow-y-auto" data-testid="swarm-final-output">{run.final_output}</pre>
        </div>
      )}
    </div>
  );
}

function AgentTask({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-white/[0.08]" data-testid={`swarm-task-${t.agent_id}`}>
      <button onClick={() => t.output && setOpen((o) => !o)} className="w-full flex items-center gap-3 p-3.5 text-left min-h-[44px]">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLORS[t.status] || "#6F7585" }} />
        <span className="text-sm font-semibold flex-1 truncate">{t.agent_name}</span>
        {t.status === "running" && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#8A52FF]" />}
        <StatusChip status={t.status} />
        {t.output && (open ? <ChevronUp className="h-4 w-4 text-white/35" /> : <ChevronDown className="h-4 w-4 text-white/35" />)}
      </button>
      {open && t.output && (
        <pre className="whitespace-pre-wrap text-xs leading-relaxed text-white/70 font-body border-t border-white/[0.06] p-4 max-h-72 overflow-y-auto">{t.output}</pre>
      )}
      {t.error && <div className="text-xs text-red-300 border-t border-white/[0.06] p-3">{t.error}</div>}
    </div>
  );
}
